/*
 * Sketch constraint solver (DOM-free, unit-tested) — Fusion-360-style semantics.
 *
 * Walls whose endpoints coincide (within WELD_TOL) share a *node*, so welded
 * corners stay welded through any solve. Constraints are enforced by iterative
 * projection (position-based, Gauss-Seidel) with LAZY WAKE: every node starts
 * asleep, and only a VIOLATED constraint wakes the nodes of its two walls.
 * A satisfied constraint moves nothing at all, so an edit can only disturb
 * geometry reachable through a chain of constraints it actually knocked out
 * of spec — everything else stays byte-identical. That is the property users
 * expect from Fusion sketches: things that don't have to move, don't move.
 *
 * Pinned walls (the dragged/edited one) have immovable nodes; opts.pinPts pins
 * single corners (the held end of an endpoint drag). If the system cannot be
 * satisfied — an over-constrained or contradictory edit — the solve FAILS and
 * writes nothing back, leaving the doc exactly as the caller staged it; the
 * caller reverts its own edit. Fusion rejects impossible edits rather than
 * settling on a drifted compromise, and so do we.
 *
 * Constraint types (doc.constraints):
 *   dist  — signed perpendicular distance from wall a's centerline to b's midpoint
 *   angle — angle from a's direction to b's direction, degrees in [0,180)
 *           (90 = perpendicular, 0 = parallel)
 *   axis  — wall a is horizontal ('h') or vertical ('v')
 *   equal — walls a and b have equal length
 * plus wall.locked (length lock), enforced here as a length constraint.
 */
import type { Constraint, Doc, Pt, Wall } from './types.ts';
import { WELD_TOL } from './geom.ts';
import { rad, uid } from './utils.ts';

export interface SkNode {
  x: number; y: number;
  ox: number; oy: number;          // position at solve start (pivot heuristics)
  inv: number;                     // 0 = pinned/out-of-scope, may never move
  awake: boolean;                  // woken by a violated constraint
  deg: number;                     // wall endpoints welded here (1 = free end)
}
export interface SkWall { wall: Wall; a: SkNode; b: SkNode; ox1: number; oy1: number; ox2: number; oy2: number }

export interface Sketch { nodes: SkNode[]; walls: Map<string, SkWall> }

export interface SolveResult { ok: boolean }

export function buildSketch(walls: Wall[], pinnedIds: Set<string>): Sketch {
  const nodes: SkNode[] = [];
  const nodeAt = (x: number, y: number): SkNode => {
    for (const n of nodes) if (Math.hypot(n.x - x, n.y - y) < WELD_TOL) { n.deg++; return n; }
    const n = { x, y, ox: x, oy: y, inv: 1, awake: false, deg: 1 };
    nodes.push(n);
    return n;
  };
  const skWalls = new Map<string, SkWall>();
  for (const w of walls) {
    skWalls.set(w.id, {
      wall: w,
      a: nodeAt(w.x1, w.y1), b: nodeAt(w.x2, w.y2),
      ox1: w.x1, oy1: w.y1, ox2: w.x2, oy2: w.y2,
    });
  }
  for (const id of pinnedIds) {
    const sw = skWalls.get(id);
    if (sw) { sw.a.inv = 0; sw.b.inv = 0; }
  }
  return { nodes, walls: skWalls };
}

/** Fold an angle into (-π/2, π/2] — walls are undirected lines. */
const foldHalf = (a: number) => {
  a = a % Math.PI;
  if (a > Math.PI / 2) a -= Math.PI;
  else if (a <= -Math.PI / 2) a += Math.PI;
  return a;
};

const wallAng = (sw: SkWall) => Math.atan2(sw.b.y - sw.a.y, sw.b.x - sw.a.x);
const wallLen = (sw: SkWall) => Math.hypot(sw.b.x - sw.a.x, sw.b.y - sw.a.y);
/** A node's effective weight this pass: pinned or still asleep ⇒ immobile. */
const wt = (n: SkNode) => (n.inv && n.awake ? 1 : 0);
const mobility = (sw: SkWall) => wt(sw.a) + wt(sw.b);
const wake = (sw: SkWall) => { sw.a.awake = true; sw.b.awake = true; };

/* ------------------------------ residuals ------------------------------ */

const resAxis = (A: SkWall, axis: 'h' | 'v') =>
  Math.abs(foldHalf(wallAng(A) - (axis === 'v' ? Math.PI / 2 : 0)));
const resAngle = (A: SkWall, B: SkWall, thetaRad: number) =>
  Math.abs(foldHalf(wallAng(B) - wallAng(A) - thetaRad));
const resEqual = (A: SkWall, B: SkWall) => Math.abs(wallLen(A) - wallLen(B));
const resLength = (sw: SkWall, L: number) => Math.abs(wallLen(sw) - L);
/** Closest point on B's SEGMENT to node p (t clamped so a T stays on the wall). */
function coinTarget(p: SkNode, B: SkWall): { x: number; y: number; t: number } {
  const bx = B.b.x - B.a.x, by = B.b.y - B.a.y;
  const L2 = bx * bx + by * by || 1e-9;
  const t = Math.max(0, Math.min(1, ((p.x - B.a.x) * bx + (p.y - B.a.y) * by) / L2));
  return { x: B.a.x + bx * t, y: B.a.y + by * t, t };
}
const resCoin = (A: SkWall, end: 0 | 1, B: SkWall) => {
  const p = end ? A.b : A.a;
  const q = coinTarget(p, B);
  return Math.hypot(q.x - p.x, q.y - p.y);
};
const resMid = (A: SkWall, end: 0 | 1, B: SkWall) => {
  const p = end ? A.b : A.a;
  return Math.hypot((B.a.x + B.b.x) / 2 - p.x, (B.a.y + B.b.y) / 2 - p.y);
};
function resDist(A: SkWall, B: SkWall, d: number): number {
  const dx = A.b.x - A.a.x, dy = A.b.y - A.a.y;
  const len = Math.hypot(dx, dy) || 1e-9;
  const nx = -dy / len, ny = dx / len;
  const s = ((B.a.x + B.b.x) / 2 - A.a.x) * nx + ((B.a.y + B.b.y) / 2 - A.a.y) * ny;
  return Math.abs(d - s);
}

/* ----------------------------- projections ----------------------------- */

/** Move a wall's movable node(s) so |ab| = L (exact when one end is held).
    When both ends could move, a free end yields before a welded corner —
    Fusion resizes a line about its attached point. */
function projLength(a: SkNode, b: SkNode, L: number): void {
  const dx = b.x - a.x, dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1e-9;
  let wA = wt(a), wB = wt(b);
  if (wA && wB) {
    if (a.deg > b.deg) wA = 0;
    else if (b.deg > a.deg) wB = 0;
  }
  const wsum = wA + wB;
  if (!wsum) return;
  const err = len - L;
  const cx = dx / len * err, cy = dy / len * err;
  a.x += cx * (wA / wsum); a.y += cy * (wA / wsum);
  b.x -= cx * (wB / wsum); b.y -= cy * (wB / wsum);
}

/**
 * Rotate a wall's movable nodes by phi. Pivot preference (Fusion-like):
 *   1. an immobile node — a rotation is the only rigid motion that keeps it put
 *   2. the corner shared with the constraint partner (rotate about the joint)
 *   3. the node that ALREADY moved this solve — that position was put there
 *      deliberately (drag/weld/another constraint), so keep it and swing the
 *      passive end into compliance instead of undoing the move
 *   4. the wall midpoint
 */
function rotWall(sw: SkWall, phi: number, prefer?: SkNode): void {
  const { a, b } = sw;
  if (!wt(a) && !wt(b)) return;
  let px: number, py: number;
  const still = (n: SkNode) => Math.abs(n.x - n.ox) < 1e-9 && Math.abs(n.y - n.oy) < 1e-9;
  if (!wt(a)) { px = a.x; py = a.y; }
  else if (!wt(b)) { px = b.x; py = b.y; }
  else if (prefer === a || prefer === b) { px = prefer.x; py = prefer.y; }
  else if (still(a) && !still(b)) { px = b.x; py = b.y; }
  else if (still(b) && !still(a)) { px = a.x; py = a.y; }
  else { px = (a.x + b.x) / 2; py = (a.y + b.y) / 2; }
  const c = Math.cos(phi), s = Math.sin(phi);
  for (const n of [a, b]) {
    if (!wt(n)) continue;
    const dx = n.x - px, dy = n.y - py;
    n.x = px + dx * c - dy * s;
    n.y = py + dx * s + dy * c;
  }
}

/** angle(B) − angle(A) = theta (mod π). */
function projAngle(A: SkWall, B: SkWall, thetaRad: number): void {
  const err = foldHalf(wallAng(B) - wallAng(A) - thetaRad);
  const mobA = mobility(A), mobB = mobility(B);
  if (!(mobA + mobB)) return;
  const shared = (A.a === B.a || A.a === B.b) ? A.a : (A.b === B.a || A.b === B.b) ? A.b : undefined;
  rotWall(A, err * mobA / (mobA + mobB), shared);
  rotWall(B, -err * mobB / (mobA + mobB), shared);
}

/** Wall lies on the horizontal ('h') or vertical ('v') axis. */
function projAxis(A: SkWall, axis: 'h' | 'v'): void {
  const err = foldHalf(wallAng(A) - (axis === 'v' ? Math.PI / 2 : 0));
  rotWall(A, -err);
}

/** Signed perpendicular distance from A's centerline to B's midpoint = d. */
function projDist(A: SkWall, B: SkWall, d: number): void {
  const dx = A.b.x - A.a.x, dy = A.b.y - A.a.y;
  const len = Math.hypot(dx, dy) || 1e-9;
  const nx = -dy / len, ny = dx / len;
  const s = ((B.a.x + B.b.x) / 2 - A.a.x) * nx + ((B.a.y + B.b.y) / 2 - A.a.y) * ny;
  const err = d - s;
  const mobA = mobility(A), mobB = mobility(B);
  if (!(mobA + mobB)) return;
  const kA = mobA / (mobA + mobB), kB = mobB / (mobA + mobB);
  // B's share moves its midpoint by exactly err·kB even when only one end is free
  for (const n of [B.a, B.b]) if (wt(n)) { n.x += nx * err * kB * (2 / mobB); n.y += ny * err * kB * (2 / mobB); }
  // A's share is applied per-node (conservative when one end is held; iterates in)
  for (const n of [A.a, A.b]) if (wt(n)) { n.x -= nx * err * kA; n.y -= ny * err * kA; }
}

/** Wall a's endpoint stays on wall b's centerline. The point and the line
    split the correction by mobility; the line's share is spread over its
    nodes so the spot under the point moves by exactly that share. */
function projCoin(A: SkWall, end: 0 | 1, B: SkWall): void {
  const p = end ? A.b : A.a;
  const q = coinTarget(p, B);
  const ex = q.x - p.x, ey = q.y - p.y;
  const mobP = wt(p), mobB = mobility(B);
  if (!(mobP + mobB)) return;
  const kP = mobP / (mobP + mobB), kB = mobB / (mobP + mobB);
  p.x += ex * kP; p.y += ey * kP;
  if (!mobB) return;
  const wa = wt(B.a) ? 1 - q.t : 0, wb = wt(B.b) ? q.t : 0;
  const denom = wa * (1 - q.t) + wb * q.t;
  if (denom > .05) {
    const s = kB / denom;
    B.a.x -= ex * s * wa; B.a.y -= ey * s * wa;
    B.b.x -= ex * s * wb; B.b.y -= ey * s * wb;
  } else {
    // point sits over an immobile end of B: translate what can move
    for (const n of [B.a, B.b]) if (wt(n)) { n.x -= ex * kB; n.y -= ey * kB; }
  }
}

/** Wall a's endpoint sits at the midpoint of wall b. */
function projMid(A: SkWall, end: 0 | 1, B: SkWall): void {
  const p = end ? A.b : A.a;
  const ex = (B.a.x + B.b.x) / 2 - p.x, ey = (B.a.y + B.b.y) / 2 - p.y;
  const mobP = wt(p), mobB = mobility(B);
  if (!(mobP + mobB)) return;
  const kP = mobP / (mobP + mobB), kB = mobB / (mobP + mobB);
  p.x += ex * kP; p.y += ey * kP;
  // shift B so its midpoint closes the rest of the gap (exact for one free end)
  for (const n of [B.a, B.b]) if (wt(n)) { n.x -= ex * kB * (2 / mobB); n.y -= ey * kB * (2 / mobB); }
}

function projEqual(A: SkWall, B: SkWall): void {
  const mobA = mobility(A), mobB = mobility(B);
  if (!(mobA + mobB)) return;
  // the more mobile wall does more of the changing; an immobile wall none
  const target = (wallLen(A) * mobB + wallLen(B) * mobA) / (mobA + mobB);
  projLength(A.a, A.b, target);
  projLength(B.a, B.b, target);
}

export const SOLVE_TOL = 1e-3;
const MAX_ITER = 1000;

/**
 * Walls reachable from `seeds` through welded corners or shared constraints.
 * Scoping a solve to this set guarantees an edit can only move geometry that
 * is physically or logically connected to what was edited — a violated
 * constraint elsewhere in the plan stays untouched until ITS component is
 * edited, instead of snapping on every unrelated change.
 */
export function affectedWalls(doc: Doc, seeds: Iterable<string>): Set<string> {
  const adj = new Map<string, string[]>();
  const link = (x: string, y: string) => {
    if (x === y) return;
    let ax = adj.get(x); if (!ax) adj.set(x, ax = []); ax.push(y);
    let ay = adj.get(y); if (!ay) adj.set(y, ay = []); ay.push(x);
  };
  const ws = doc.walls;
  for (let i = 0; i < ws.length; i++) {
    for (let j = i + 1; j < ws.length; j++) {
      const a = ws[i], b = ws[j];
      let welded = false;
      for (const [ax2, ay2] of [[a.x1, a.y1], [a.x2, a.y2]] as const)
        for (const [bx2, by2] of [[b.x1, b.y1], [b.x2, b.y2]] as const)
          if (Math.hypot(ax2 - bx2, ay2 - by2) < WELD_TOL) welded = true;
      if (welded) link(a.id, b.id);
    }
  }
  for (const c of doc.constraints || []) if ('b' in c) link(c.a, c.b);
  const out = new Set<string>();
  const stack = [...seeds];
  while (stack.length) {
    const id = stack.pop()!;
    if (out.has(id)) continue;
    out.add(id);
    for (const n of adj.get(id) || []) if (!out.has(n)) stack.push(n);
  }
  return out;
}

/**
 * Enforce all constraints on doc.walls in place. `pinned` = wall ids that must
 * not move (typically the wall being dragged or edited). `seeds` (optional)
 * limits the solve to walls connected to them via welds/constraints; anything
 * outside that component is frozen and its constraints skipped entirely.
 * opts.pinPts pins the node at each given point (e.g. the held end of a drag).
 *
 * Returns {ok:false} — writing NOTHING back — when the system cannot be
 * satisfied; the caller reverts whatever it staged.
 */
export function solveConstraints(doc: Doc, pinned: Set<string>, seeds?: Iterable<string>,
                                 opts?: { pinPts?: Pt[] }): SolveResult {
  const csAll = doc.constraints || [];
  const lockedAll = doc.walls.filter(w => w.locked);
  if (!csAll.length && !lockedAll.length) return { ok: true };

  const sk = buildSketch(doc.walls, pinned);
  let scope: Set<string> | null = null;
  if (seeds) {
    scope = affectedWalls(doc, seeds);
    for (const [id, sw] of sk.walls) {
      if (!scope.has(id)) { sw.a.inv = 0; sw.b.inv = 0; }
    }
  }
  for (const p of opts?.pinPts || []) {
    for (const n of sk.nodes)
      if (Math.hypot(n.x - p.x, n.y - p.y) < WELD_TOL) n.inv = 0;
  }
  // constraints never straddle the scope boundary (they define connectivity),
  // so out-of-scope ones can simply be skipped
  const inScope = (id: string) => !scope || scope.has(id);
  const cs = csAll.filter(c => inScope(c.a) && (!('b' in c) || inScope(c.b)));
  const lockedLens = lockedAll.filter(w => inScope(w.id))
    .map(w => ({ sw: sk.walls.get(w.id)!, len: Math.hypot(w.x2 - w.x1, w.y2 - w.y1) }));

  // Two-tier tolerance: asleep geometry is only recruited by a REAL violation
  // (SOLVE_TOL — this is the no-drift guarantee), but once awake its
  // constraints are polished to FINE so cascaded solves leave no residue
  // (a wall re-squared through three neighbors still ends up exactly square).
  const FINE = 1e-6;
  let settled = false;
  let checkpointErr = Infinity;
  for (let iter = 0; iter < MAX_ITER && !settled; iter++) {
    settled = true;
    let passErr = 0;   // worst residual acted on this pass (for stall detection)
    for (const c of cs) {
      const A = sk.walls.get(c.a); if (!A) continue;
      if (c.type === 'fix') {
        // grounded wall: nodes belong at the stored coords, exactly
        const tgts = [[A.a, c.x1, c.y1], [A.b, c.x2, c.y2]] as const;
        let fErr = 0;
        for (const [n, tx, ty] of tgts) fErr = Math.max(fErr, Math.hypot(n.x - tx, n.y - ty));
        if (fErr <= (A.a.awake || A.b.awake ? FINE : SOLVE_TOL)) continue;
        if (fErr > SOLVE_TOL) wake(A);
        passErr = Math.max(passErr, fErr);
        for (const [n, tx, ty] of tgts) {
          const e = Math.hypot(n.x - tx, n.y - ty);
          if (e <= FINE) continue;
          if (!wt(n)) { if (e > SOLVE_TOL) return { ok: false }; continue; }
          n.x = tx; n.y = ty; settled = false;
        }
        continue;
      }
      const B = c.type === 'axis' ? null : sk.walls.get(c.b);
      if (c.type !== 'axis' && !B) continue;
      const err = c.type === 'axis' ? resAxis(A, c.axis)
        : c.type === 'dist' ? resDist(A, B!, c.dist)
        : c.type === 'angle' ? resAngle(A, B!, rad(c.deg))
        : c.type === 'coin' ? resCoin(A, c.end, B!)
        : c.type === 'mid' ? resMid(A, c.end, B!)
        : c.type === 'colin' ? Math.max(resAngle(A, B!, 0), resDist(A, B!, 0))
        : resEqual(A, B!);
      const awake = A.a.awake || A.b.awake || (B ? B.a.awake || B.b.awake : false);
      if (err <= (awake ? FINE : SOLVE_TOL)) continue;
      if (err > SOLVE_TOL) { wake(A); if (B) wake(B); }
      const mob = c.type === 'coin' || c.type === 'mid'
        ? wt(c.end ? A.b : A.a) + mobility(B!)       // only the attached end can fix these
        : mobility(A) + (B ? mobility(B) : 0);
      if (!mob) {
        if (err > SOLVE_TOL) return { ok: false };   // violated, nothing may move
        continue;                                    // sub-coarse residue on held geometry
      }
      settled = false;
      passErr = Math.max(passErr, err);
      if (c.type === 'axis') projAxis(A, c.axis);
      else if (c.type === 'dist') projDist(A, B!, c.dist);
      else if (c.type === 'angle') projAngle(A, B!, rad(c.deg));
      else if (c.type === 'coin') projCoin(A, c.end, B!);
      else if (c.type === 'mid') projMid(A, c.end, B!);
      else if (c.type === 'colin') { projAngle(A, B!, 0); projDist(A, B!, 0); }
      else projEqual(A, B!);
    }
    for (const { sw, len } of lockedLens) {
      const err = resLength(sw, len);
      if (err <= (sw.a.awake || sw.b.awake ? FINE : SOLVE_TOL)) continue;
      if (err > SOLVE_TOL) wake(sw);
      if (!mobility(sw)) {
        if (err > SOLVE_TOL) return { ok: false };
        continue;
      }
      settled = false;
      passErr = Math.max(passErr, err);
      projLength(sw.a, sw.b, len);
    }
    // stall detection: a fighting (over-constrained) system ping-pongs without
    // progress — bail out early instead of burning the whole iteration budget
    // on every drag frame. Converging systems shrink far faster than 5%/32.
    if (!settled && (iter & 31) === 31) {
      if (passErr > checkpointErr * 0.95) return { ok: false };
      checkpointErr = passErr;
    }
  }
  if (!settled) return { ok: false };   // contradictory system: refuse, don't compromise

  // write back only walls that actually moved, with fp noise rounded away
  const r4 = (v: number) => Math.round(v * 1e4) / 1e4;
  for (const sw of sk.walls.values()) {
    if (Math.abs(sw.a.x - sw.ox1) < 1e-7 && Math.abs(sw.a.y - sw.oy1) < 1e-7 &&
        Math.abs(sw.b.x - sw.ox2) < 1e-7 && Math.abs(sw.b.y - sw.oy2) < 1e-7) continue;
    sw.wall.x1 = r4(sw.a.x); sw.wall.y1 = r4(sw.a.y);
    sw.wall.x2 = r4(sw.b.x); sw.wall.y2 = r4(sw.b.y);
  }
  return { ok: true };
}

/* ------------------------- snapshot / revert ------------------------- */

export interface WallSnap { w: Wall; x1: number; y1: number; x2: number; y2: number }
/** Cheap coordinate snapshot, for reverting a staged edit the solver rejects. */
export const snapshotWalls = (doc: Doc): WallSnap[] =>
  doc.walls.map(w => ({ w, x1: w.x1, y1: w.y1, x2: w.x2, y2: w.y2 }));
export const restoreWalls = (snap: WallSnap[]): void => {
  for (const r of snap) { r.w.x1 = r.x1; r.w.y1 = r.y1; r.w.x2 = r.x2; r.w.y2 = r.y2; }
};

/**
 * Change a wall's thickness while preserving the CLEAR span of any distance
 * constraints it participates in: the stored centerline distance grows/shrinks
 * by the half-thickness change, then the sketch re-solves (the edited wall
 * stays put, its partners shift). Returns false — with everything reverted —
 * if the adjusted distances can't be satisfied.
 */
export function setWallThickness(doc: Doc, wl: Wall, th: number): boolean {
  const oldTh = wl.th;
  const touched: { c: { dist: number }; dist: number }[] = [];
  wl.th = th;
  const dth = (th - oldTh) / 2;
  for (const c of doc.constraints || []) {
    if (c.type === 'dist' && (c.a === wl.id || c.b === wl.id)) {
      touched.push({ c, dist: c.dist });
      c.dist += (c.dist < 0 ? -1 : 1) * dth;
    }
  }
  if (!solveConstraints(doc, new Set([wl.id]), [wl.id]).ok) {
    wl.th = oldTh;
    for (const t of touched) t.c.dist = t.dist;
    return false;
  }
  return true;
}

/**
 * Fusion-style auto-constraints for a freshly drawn wall: an exactly
 * horizontal/vertical segment gets an axis constraint; a segment drawn on a
 * ⊥/∥ snap to a reference wall gets that angle constraint (axis wins when
 * both apply — it says the same thing without chaining through the
 * reference); an endpoint that edge-snapped onto another wall's span gets a
 * coincident tie to that host, so the T-junction survives later edits.
 */
export interface DrawHost { id: string; mid?: boolean }
export function inferDrawConstraints(w: Wall, ref?: { id: string; rel: '⊥' | '∥' } | null,
                                     hosts?: { start?: DrawHost; end?: DrawHost }): Constraint[] {
  const out: Constraint[] = [];
  const dx = w.x2 - w.x1, dy = w.y2 - w.y1;
  if (Math.abs(dy) < 1e-9 && Math.abs(dx) > 1e-9) out.push({ id: uid('c'), type: 'axis', a: w.id, axis: 'h' });
  else if (Math.abs(dx) < 1e-9 && Math.abs(dy) > 1e-9) out.push({ id: uid('c'), type: 'axis', a: w.id, axis: 'v' });
  else if (ref) out.push({ id: uid('c'), type: 'angle', a: ref.id, b: w.id, deg: ref.rel === '⊥' ? 90 : 0 });
  // Auto-ties are always the sliding ⚯ kind — a ⋈ midpoint tie pins the end
  // to the host's CENTER, which fights every later drag of the wall (the ⋈
  // is explicit-only, via the Constrain tool). The midpoint SNAP still places
  // the end exactly at the middle; it just doesn't chain it there.
  for (const [end, h] of [[0, hosts?.start], [1, hosts?.end]] as const)
    if (h && h.id !== w.id)
      out.push({ id: uid('c'), type: 'coin', a: w.id, end, b: h.id });
  return out;
}

/**
 * After DROPPING a wall end onto a host (edge/midpoint snap), record the
 * direction the drop landed with — like drawing does. Exactly H/V ⇒ axis;
 * exactly square to the host ⇒ a 90° angle to it. Skips anything the wall
 * already has; tolerances are loose enough to survive coordinate rounding.
 */
export function inferDropConstraints(doc: Doc, w: Wall, host: Wall): Constraint[] {
  const cs = doc.constraints || [];
  if (cs.some(c => (c.type === 'axis' || c.type === 'fix') && c.a === w.id)) return [];
  const dx = w.x2 - w.x1, dy = w.y2 - w.y1;
  if (Math.abs(dy) < 1e-3 && Math.abs(dx) > 1e-3) return [{ id: uid('c'), type: 'axis', a: w.id, axis: 'h' }];
  if (Math.abs(dx) < 1e-3 && Math.abs(dy) > 1e-3) return [{ id: uid('c'), type: 'axis', a: w.id, axis: 'v' }];
  if (cs.some(c => (c.type === 'angle' || c.type === 'colin') && 'b' in c &&
      ((c.a === w.id && c.b === host.id) || (c.a === host.id && c.b === w.id)))) return [];
  const cross = Math.abs(dx * (host.y2 - host.y1) - dy * (host.x2 - host.x1));
  const dot = Math.abs(dx * (host.x2 - host.x1) + dy * (host.y2 - host.y1));
  if (dot < cross * 1e-3) return [{ id: uid('c'), type: 'angle', a: host.id, b: w.id, deg: 90 }];
  return [];
}
